import { useMemo } from "react";
import type { BrandPriceStats } from "@/types/generated";

/**
 * Estimate percentile from quartiles (simplified distribution assumption)
 */
function estimatePercentile(
  priceUsd: number | null | undefined,
  q1: number | null | undefined,
  median: number | null | undefined,
  q3: number | null | undefined
): number {
  if (!priceUsd) return 0.5;

  // If we have quartiles, estimate position
  if (q1 && median && q3) {
    if (priceUsd <= q1) {
      // Below Q1: interpolate between 0 and 0.25
      return 0.125 * (priceUsd / Math.max(1, q1));
    }
    if (priceUsd <= median) {
      // Q1 to median: interpolate between 0.25 and 0.5
      return 0.25 + 0.25 * ((priceUsd - q1) / Math.max(1, median - q1));
    }
    if (priceUsd <= q3) {
      // Median to Q3: interpolate between 0.5 and 0.75
      return 0.5 + 0.25 * ((priceUsd - median) / Math.max(1, q3 - median));
    }
    // Above Q3: interpolate between 0.75 and 1.0
    const excess = priceUsd - q3;
    return Math.min(0.99, 0.75 + 0.25 * (excess / Math.max(1, q3)));
  }

  // Fallback: assume middle
  return 0.5;
}

/**
 * Map percentile to price insight (returns null for fair price range)
 * Note: Card uses more detailed thresholds ("Great deal", "Priced high", "Very high")
 */
function getPriceInsight(
  percentile: number
): { label: string; color: string; weight: number; ariaLabel: string } | null {
  // Keep "deal" and "high" labels relatively strict so "fair" covers the majority of listings.
  if (percentile <= 0.25) {
    return {
      label: "Great deal",
      color: "green.7",
      weight: 500,
      ariaLabel: "Price insight: Great deal. Activate for details.",
    };
  }
  if (percentile <= 0.85) {
    return null; // Fair price - don't show anything
  }
  if (percentile <= 0.95) {
    return {
      label: "Priced high",
      color: "orange.7",
      weight: 400,
      ariaLabel: "Price insight: Priced high. Activate for details.",
    };
  }
  return {
    label: "Very high",
    color: "red.7",
    weight: 400,
    ariaLabel: "Price insight: Very high. Activate for details.",
  };
}

interface PriceInsight {
  label: string;
  color: string;
  weight: number;
  ariaLabel: string;
}

interface PriceInsightsResult {
  /** The relevant stats (category-specific if available, otherwise brand-wide) */
  stats: BrandPriceStats | null;
  /** Name for comparison display (e.g., "tops" or "Acne Studios") */
  comparisonName: string;
  /** Estimated percentile (0-1) */
  percentile: number;
  /** Price insight if outside fair range, null otherwise */
  insight: PriceInsight | null;
  /** Whether price is in fair range (no insight badge shown) */
  isFairPrice: boolean;
  /** Q1 price */
  q1: number | null;
  /** Q3 price */
  q3: number | null;
  /** Whether stats are available for this item */
  hasStats: boolean;
  /** Whether category-specific stats are being used */
  usingCategoryStats: boolean;
}

/**
 * Hook for calculating price insights from brand/category statistics.
 * Uses category-specific stats when available (>=5 samples), otherwise falls back to brand-wide stats.
 */
export function usePriceInsights(item: SearchResultItem | null | undefined): PriceInsightsResult {
  return useMemo(() => {
    const defaultResult: PriceInsightsResult = {
      stats: null,
      comparisonName: "this brand",
      percentile: 0.5,
      insight: null,
      isFairPrice: false,
      q1: null,
      q3: null,
      hasStats: false,
      usingCategoryStats: false,
    };

    if (!item || !item.brand) {
      return defaultResult;
    }

    // Skip price insights for rentable and auction items
    if (item.rentable || item.saletype === 2) {
      return defaultResult;
    }

    // Skip price insights for independent sellers (VENDOR_TYPES[0] in
    // lib/shared/constants.ts) — not shown for this vendor type by design.
    if (item.vendor?.type === 0) {
      return defaultResult;
    }

    // Use category-specific stats if available, otherwise brand-wide
    const hasKnownCategory = !!item.itemtype && item.itemtype !== "unknown";
    const usingCategoryStats = !!item.brand.categoryStats && hasKnownCategory;
    const stats = item.brand.categoryStats || item.brand.brandStats;

    if (!stats?.medianPrice || !item.priceusd) {
      return defaultResult;
    }

    const percentile = estimatePercentile(
      item.priceusd,
      stats.q1Price,
      stats.medianPrice,
      stats.q3Price
    );

    const insight = getPriceInsight(percentile);
    const comparisonName = usingCategoryStats ? item.itemtype : item.brand?.label || "this brand";

    return {
      stats,
      comparisonName,
      percentile,
      insight,
      isFairPrice: !insight,
      q1: stats.q1Price || null,
      q3: stats.q3Price || null,
      hasStats: true,
      usingCategoryStats,
    };
  }, [item]);
}
